import React, { useState, useEffect, useRef } from "react";
import { Box, useMediaQuery, useTheme, Button } from "@mui/material";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useEnhancedSession } from "../hooks/useEnhancedSession";
import { SmartToy as AIChatIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

interface LayoutProps {
  children: React.ReactNode;
  sidebarKey?: string;
}

const SIDEBAR_WIDTH = 100; // Changed from 240 to match current design
const HEADER_HEIGHT = 64;

const Layout: React.FC<LayoutProps> = ({ children, sidebarKey }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);

  // Chat button position state
  const [chatButtonPosition, setChatButtonPosition] = useState(() => {
    const saved = localStorage.getItem("chatButtonPosition");
    return saved ? JSON.parse(saved) : { bottom: 24, right: 24 };
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const chatButtonRef = useRef<HTMLButtonElement>(null);

  // Initialize enhanced session management
  useEnhancedSession();

  const toggleSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();

  // Save position to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      "chatButtonPosition",
      JSON.stringify(chatButtonPosition)
    );
  }, [chatButtonPosition]);

  // Handle drag start
  const handleDragStart = (clientX: number, clientY: number) => {
    setIsDragging(true);
    setDragStart({ x: clientX, y: clientY });
  };

  // Handle drag move
  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isDragging) return;

    const deltaX = clientX - dragStart.x;
    const deltaY = dragStart.y - clientY;

    const newPosition = {
      right: Math.max(
        8,
        Math.min(window.innerWidth - 64, chatButtonPosition.right - deltaX)
      ),
      bottom: Math.max(
        8,
        Math.min(
          window.innerHeight - 64 - HEADER_HEIGHT,
          chatButtonPosition.bottom + deltaY
        )
      ),
    };

    setChatButtonPosition(newPosition);
    setDragStart({ x: clientX, y: clientY });
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: MouseEvent) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Add/remove global event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, dragStart, chatButtonPosition]);

  // Handle click vs drag
  const handleChatButtonClick = (e: React.MouseEvent) => {
    if (!isDragging) {
      navigate("/chat");
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* Header - Full width */}
      <Header onMenuClick={toggleSidebar} />

      {/* Content wrapper */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          mt: `${HEADER_HEIGHT}px`,
        }}
      >
        {/* Sidebar */}
        <Sidebar
          isOpen={isMobile ? isSidebarOpen : true} // Always true for desktop
          onClose={() => setIsSidebarOpen(false)}
          isMobile={isMobile}
          sidebarKey={sidebarKey}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: {
              xs: "100%",
              sm: `calc(100% - ${SIDEBAR_WIDTH}px)`,
            },
            transition: theme.transitions.create(["margin", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            height: "100%",
            overflow: "auto",
            bgcolor: "background.default",
          }}
        >
          {children}
        </Box>
      </Box>
      {/* Floating Chat Button - Hidden on chat page */}
      {location.pathname !== "/chat" && (
        <Box
          sx={{
            position: "fixed",
            bottom: chatButtonPosition.bottom,
            right: chatButtonPosition.right,
            zIndex: 1000,
          }}
        >
          <Button
            ref={chatButtonRef}
            variant="contained"
            color="primary"
            onClick={handleChatButtonClick}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            sx={{
              minWidth: "auto",
              width: 56,
              height: 56,
              borderRadius: "50%",
              boxShadow: isDragging
                ? "0 8px 20px rgba(0, 0, 0, 0.3)"
                : "0 4px 12px rgba(0, 0, 0, 0.15)",
              transition: isDragging ? "none" : "all 0.3s ease-in-out",
              cursor: isDragging ? "grabbing" : "grab",
              userSelect: "none",
              "&:hover": {
                transform: isDragging ? "none" : "scale(1.1)",
                boxShadow: isDragging
                  ? "0 8px 20px rgba(0, 0, 0, 0.3)"
                  : "0 6px 16px rgba(0, 0, 0, 0.2)",
              },
              "&:active": {
                transform: isDragging ? "none" : "scale(0.95)",
              },
            }}
            aria-label="Chat with GaadiKharcha Ai (Draggable)"
            title="Chat with GaadiKharcha Ai"
          >
            <AIChatIcon sx={{ fontSize: 28 }} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Layout;
